import * as React from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Inbox, Loader2 } from "lucide-react";
import { Button } from "@bcc/components/ui/button";
import { cn } from "@bcc/lib/utils";
import { setEmptyStateTestId, setErrorStateTestId, setRetryButtonTestId, setLoadingRowTestId } from "./data-table.utils";

interface DataTableEmptyStateProps {
  /** Number of columns (for colspan) */
  colSpan: number;
  /** Type of empty state */
  type: "empty" | "loading" | "error";
  /** Custom message to display (overrides i18n default) */
  message?: string;
  /** Callback for retry button (error state only) */
  onRetry?: () => void;
}

/**
 * DataTableEmptyState
 *
 * Renders empty, loading, or error states for the DataTable.
 */
export function DataTableEmptyState({ colSpan, type, message, onRetry }: DataTableEmptyStateProps) {
  const { t } = useTranslation();

  const texts = React.useMemo(
    () => ({
      noResults: t("dataTable.emptyState.noResults"),
      error: t("dataTable.emptyState.error"),
      loading: t("dataTable.emptyState.loading"),
      retry: t("dataTable.emptyState.retry"),
    }),
    [t]
  );

  return (
    <tr>
      <td colSpan={colSpan} className="h-48">
        <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
          {type === "loading" && (
            <>
              <Loader2 className="size-8 animate-spin mb-3" />
              <p className="text-sm">{message || texts.loading}</p>
            </>
          )}

          {type === "empty" && (
            <div data-testid={setEmptyStateTestId()}>
              <Inbox className="size-12 mb-3 opacity-50 mx-auto" />
              <p className="text-sm">{message || texts.noResults}</p>
            </div>
          )}

          {type === "error" && (
            <div data-testid={setErrorStateTestId()}>
              <AlertCircle className="size-8 mb-3 text-destructive mx-auto" />
              <p className="text-sm text-destructive mb-3">{message || texts.error}</p>
              {onRetry && (
                <Button data-testid={setRetryButtonTestId()} variant="outline" size="sm" onClick={onRetry}>
                  {texts.retry}
                </Button>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * Loading skeleton rows
 */
export function DataTableLoadingRows({
  colSpan,
  rowCount = 5,
  hasSelection = false,
  hasExpander = false,
}: {
  colSpan: number;
  rowCount?: number;
  hasSelection?: boolean;
  hasExpander?: boolean;
}) {
  return (
    <>
      {Array.from({ length: rowCount }).map((_, i) => (
        <tr key={i} data-testid={setLoadingRowTestId(i)} className="border-b border-border">
          {hasExpander && (
            <td className="w-[50px] px-4 py-4">
              <div className="size-4 bg-gray-200 rounded animate-pulse" />
            </td>
          )}
          {hasSelection && (
            <td className="w-[50px] px-4 py-4">
              <div className="size-4 bg-gray-200 rounded animate-pulse" />
            </td>
          )}
          {Array.from({ length: colSpan }).map((_, j) => (
            <td key={j} className="px-4 py-4">
              <div className={cn("h-4 bg-gray-200 rounded animate-pulse", j === 0 ? "w-32" : "w-24")} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
